import { useEffect, useMemo, useState } from "react";
import api from "../api";
import { getLeaderboardRank } from "../utils/leaderboardUtils";
import { getSocialPlatforms, matchPlatform } from "../utils/socialPlatformUtils";
import { Link } from "react-router-dom";
import { resolveMediaUrl } from "../utils/url";

export default function UserProfile({ userId }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState([]);
  const [badges, setBadges] = useState([]);
  const [points, setPoints] = useState({ lifetime: 0, weekly: 0 });
  const [lifetimeRank, setLifetimeRank] = useState(null);
  const [weeklyRank, setWeeklyRank] = useState(null);
  const [livePlayer, setLivePlayer] = useState(null); // { kind, title, src, link }
  const [socialContent, setSocialContent] = useState(null); // { twitch:{vods,clips}, youtube:{videos}, tiktok, instagram }

  const parentHost = useMemo(() => {
    try { return window.location.hostname; } catch { return "localhost"; }
  }, []);

  // Ensure proxied image URLs (/social-content/...) go to API origin, not the Vite dev server
  const resolveImageUrl = (u) => {
    try {
      if (!u || typeof u !== 'string') return u;
      if (u.startsWith('/')) {
        const base = (api?.defaults?.baseURL || '').replace(/\/$/, '');
        if (base) return base + u;
      }
      return u;
    } catch { return u; }
  };

  function parseTwitchChannel(url) {
    try {
      if (!url) return null;
      const u = new URL(url);
      const host = u.hostname.toLowerCase();
      if (!/twitch\.tv$/.test(host) && !/twitch\.tv$/.test(host.replace(/^www\./,''))) return null;
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length === 0) return null;
      // First segment is channel (exclude special paths like videos)
      const first = parts[0].toLowerCase();
      if (["videos", "directory", "p"].includes(first)) return null;
      return first;
    } catch { return null; }
  }

  function findTwitchFromSocialLinks(links) {
    if (!Array.isArray(links)) return { channel: null, link: null };
    for (const l of links) {
      if (!l || !l.url) continue;
      const lower = (l.name || "").toLowerCase();
      if (lower.includes("twitch") || /twitch\.tv/.test(l.url)) {
        const ch = parseTwitchChannel(l.url);
        if (ch) return { channel: ch, link: `https://www.twitch.tv/${ch}` };
      }
    }
    return { channel: null, link: null };
  }

  // YouTube helpers
  function parseYouTubeInfo(url) {
    try {
      if (!url) return { videoId: null, channelId: null };
      const u = new URL(url);
      const host = u.hostname.toLowerCase();
      // support www.youtube.com, m.youtube.com, youtu.be, plus localized params (hl=de etc.)
      if (!/(^|\.)youtube\.com$/.test(host) && !host.includes('youtu.be')) return { videoId: null, channelId: null };
      // video id from v param
      const v = u.searchParams.get('v');
      if (v) return { videoId: v, channelId: null };
      // youtu.be short link
      if (host.includes('youtu.be')) {
        const parts = u.pathname.split('/').filter(Boolean);
        if (parts[0]) return { videoId: parts[0], channelId: null };
      }
      // youtube live short path: /live/<videoId>
      {
        const parts = u.pathname.split('/').filter(Boolean);
        if (parts[0] && parts[0].toLowerCase() === 'live' && parts[1]) {
          return { videoId: parts[1], channelId: null };
        }
      }
      // channel id from /channel/UCxxxx
      const parts = u.pathname.split('/').filter(Boolean);
      const chIdx = parts.findIndex(p => p.toLowerCase() === 'channel');
      if (chIdx !== -1 && parts[chIdx+1]) return { videoId: null, channelId: parts[chIdx+1] };
      // sometimes /live on channel
      if (parts[0] && parts[0].startsWith('@')) {
        // handle-based channels cannot be embedded without channelId
        return { videoId: null, channelId: null };
      }
      return { videoId: null, channelId: null };
    } catch {
      return { videoId: null, channelId: null };
    }
  }

  function findYouTubeFromSocialLinks(links) {
    if (!Array.isArray(links)) return { videoId: null, channelId: null, link: null };
    for (const l of links) {
      if (!l || !l.url) continue;
      if (/youtu\.be|youtube\.com/.test(l.url)) {
        const info = parseYouTubeInfo(l.url);
        if (info.videoId || info.channelId) return { ...info, link: l.url };
      }
    }
    return { videoId: null, channelId: null, link: null };
  }

  // TikTok helpers
  function parseTikTokUsername(url) {
    try {
      if (!url) return null;
      const u = new URL(url);
      const host = u.hostname.toLowerCase();
      if (!host.includes('tiktok.com')) return null;
      const parts = u.pathname.split('/').filter(Boolean);
      // Expect /@username[/live]
      const at = parts.find(p => p.startsWith('@'));
      if (at) return at.replace(/^@/, '');
      return null;
    } catch { return null; }
  }
  function findTikTokFromSocialLinks(links) {
    if (!Array.isArray(links)) return { username: null, link: null };
    for (const l of links) {
      if (!l || !l.url) continue;
      if (l.url.includes('tiktok.com')) {
        const username = parseTikTokUsername(l.url);
        if (username) return { username, link: l.url };
      }
    }
    return { username: null, link: null };
  }

  const [profileError, setProfileError] = useState('');
  useEffect(() => {
    setProfileError('');
    api.get(`/admin/u/${userId}`).then(res => {
      setUser(res.data);
      if (res.data && res.data.points) setPoints(res.data.points);
      setLoading(false);
    }).catch(err => {
      const msg = err?.response?.data?.error || 'Profil kann nicht angezeigt werden.';
      setProfileError(msg);
      setLoading(false);
    });
    getSocialPlatforms().then(setPlatforms);
    api.get(`/badges/user/${userId}`).then(res => setBadges(res.data));
    // Leaderboard-Platzierungen holen
    getLeaderboardRank(userId, 'lifetime').then(setLifetimeRank);
    getLeaderboardRank(userId, 'weekly').then(setWeeklyRank);
  }, [userId]);

  const [shops, setShops] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async ()=>{
      try {
        const res = await api.get(`/shops/by-user/${userId}`);
        if (!cancelled) setShops(res.data?.items || []);
      } catch {
        if (!cancelled) setShops([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // Check if user is currently live via SupportPlan (Twitch, YouTube, TikTok)
  useEffect(() => {
    let cancelled = false;
    async function checkLive() {
      try {
        const nowIso = new Date().toISOString();
        const res = await api.get('/supportplan/entries', { params: { weekStart: nowIso, weekEnd: nowIso } });
        const items = Array.isArray(res.data) ? res.data : [];
        const userEntries = items.filter(e => e && e.userId === Number(userId));
        let live = null;
        for (const e of userEntries) {
          const plat = (e.platform || '').toLowerCase();
          const link = typeof e.streamLink === 'string' ? e.streamLink : '';
          // Twitch
          if (!live && (plat.includes('twitch') || /twitch\.tv/.test(link))) {
            let channel = parseTwitchChannel(link);
            if (!channel && user?.socialLinks) channel = findTwitchFromSocialLinks(user.socialLinks).channel;
            if (channel) {
              // Optional verification via backend API
              if (import.meta.env.VITE_LIVE_VERIFY === 'true') {
                try {
                  const v = await api.post('/live/check', { platform: 'twitch', twitchChannel: channel });
                  if (!v?.data?.live) { continue; }
                } catch {}
              }
              const src = `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${encodeURIComponent(parentHost)}&muted=false&autoplay=false`;
              live = { kind: 'twitch', title: 'Live auf Twitch', src, link: link || `https://www.twitch.tv/${channel}` };
              break;
            }
          }
          // YouTube
          if (!live && (plat.includes('youtube') || /youtu\.be|youtube\.com/.test(link))) {
            let info = parseYouTubeInfo(link);
            if (!info.videoId && !info.channelId && user?.socialLinks) info = findYouTubeFromSocialLinks(user.socialLinks);
            if (info.videoId) {
              if (import.meta.env.VITE_LIVE_VERIFY === 'true') {
                try {
                  const v = await api.post('/live/check', { platform: 'youtube', youtubeVideoId: info.videoId });
                  if (!v?.data?.live) { continue; }
                } catch {}
              }
              const src = `https://www.youtube.com/embed/${encodeURIComponent(info.videoId)}?autoplay=0`;
              live = { kind: 'youtube', title: 'Live auf YouTube', src, link: link || `https://www.youtube.com/watch?v=${info.videoId}` };
              break;
            } else if (info.channelId) {
              if (import.meta.env.VITE_LIVE_VERIFY === 'true') {
                try {
                  const v = await api.post('/live/check', { platform: 'youtube', youtubeChannelId: info.channelId });
                  if (!v?.data?.live) { continue; }
                } catch {}
              }
              const src = `https://www.youtube.com/embed/live_stream?channel=${encodeURIComponent(info.channelId)}&autoplay=0`;
              live = { kind: 'youtube', title: 'Live auf YouTube', src, link: link || `https://www.youtube.com/channel/${info.channelId}/live` };
              break;
            }
          }
          // TikTok
          if (!live && (plat.includes('tiktok') || /tiktok\.com/.test(link))) {
            let username = parseTikTokUsername(link);
            if (!username && user?.socialLinks) username = findTikTokFromSocialLinks(user.socialLinks).username;
            if (username) {
              if (import.meta.env.VITE_LIVE_VERIFY === 'true') {
                try {
                  const v = await api.post('/live/check', { platform: 'tiktok', tiktokUsername: username });
                  if (!v?.data?.live) { continue; }
                } catch {}
              }
              const src = `https://www.tiktok.com/embed/live/${encodeURIComponent(username)}`;
              live = { kind: 'tiktok', title: 'Live auf TikTok', src, link: link || `https://www.tiktok.com/@${username}/live` };
              break;
            }
          }
        }
        if (!cancelled) setLivePlayer(live);
      } catch (e) {
        // silently ignore (e.g., not authenticated)
        if (!cancelled) setLivePlayer(null);
      }
    }
    checkLive();
    const t = setInterval(checkLive, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [userId, user?.socialLinks]);

  // Load recent social content (VoDs, Clips, YouTube Videos/Shorts, etc.)
  useEffect(() => {
    let cancelled = false;
    async function loadContent() {
      try {
        // allow forcing a cache-bypass for debugging via ?nocache=1 on the page URL
        let nocache = false;
        try { const sp = new URLSearchParams(window.location.search); nocache = sp.get('nocache') === '1' || sp.get('nocache') === 'true'; } catch {}
        const { data } = await api.get(`/social-content/user/${userId}`, { params: nocache ? { nocache: 1 } : undefined });
        if (!cancelled) setSocialContent(data || null);
      } catch (e) {
        if (!cancelled) setSocialContent(null);
      }
    }
    loadContent();
    const id = setInterval(loadContent, 5 * 60 * 1000); // refresh every 5 min
    return () => { cancelled = true; clearInterval(id); };
  }, [userId]);

  if (loading) return <div style={{textAlign:'center',marginTop:40}}>Lade Profil ...</div>;
  if (!user) return <div style={{textAlign:'center',marginTop:40}}>{profileError || 'Profil nicht gefunden.'}</div>;

  return (
    <div className="home-page">
      <section style={{width:'100vw',margin:0,padding:50}}>
        <div style={{marginBottom:24}}>
          {/* Avatar zentriert über dem Content */}
          <div style={{display:'flex', justifyContent:'center', marginBottom:16}}>
            <img
              src={resolveMediaUrl(user.avatarUrl || '/avatars/default-avatar.png')}
              alt="Avatar"
              style={{
                width:120, height:120, borderRadius:'50%', objectFit:'cover',
                border:'2px solid #32CD89', boxShadow:'0 2px 8px rgba(0,0,0,0.25)'
              }}
            />
          </div>
          {/* Hauptinhalt */}
          <div style={{maxWidth:900, margin:'0 auto', textAlign:'center'}}>
            <div style={{display:'flex', alignItems:'flex-start', gap:32, flexWrap:'wrap', justifyContent:'center'}}>
              <div style={{flex:1, minWidth:260}}>
            <h2 style={{fontWeight:700, marginBottom:4}}>{user.displayName || user.username}</h2>
            <div style={{opacity:0.7, marginBottom:8}}>@{user.username}</div>
            <div style={{marginBottom:12, whiteSpace:'pre-line'}}>{user.bio}</div>
            <div style={{marginBottom:12, display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap'}}>
              <Link
                to={`/supportplan?view=week&scope=personal&userId=${encodeURIComponent(user.id)}`}
                style={{
                      background:'#32CD89', color:'#222', padding:'8px 16px', borderRadius:8, display:'inline-block',
                  textDecoration:'none', fontWeight:600, boxShadow:'0 2px 8px #0002'
                }}
                title={`Streamplan von ${user.displayName || user.username} ansehen`}
              >
                {(user.displayName || user.username)}'s Streamplan ansehen
              </Link>
              <Link
                to="/chat"
                style={{
                  background:'#2a2a2a', color:'#fff', padding:'8px 16px', borderRadius:8, display:'inline-block',
                  textDecoration:'none', fontWeight:600, border:'1px solid #444', boxShadow:'0 2px 8px #0002'
                }}
                title="Privaten Chat öffnen"
              >
                Privaten Chat öffnen
              </Link>
            </div>
            <div style={{display:'flex', gap:12, flexWrap:'wrap', marginBottom:8, justifyContent:'center'}}>
              {Array.isArray(user.socialLinks) && user.socialLinks.length > 0 ? (
                user.socialLinks.map((link, idx) => link.url && (
                  <a key={idx} href={link.url} target="_blank" rel="noopener" style={{background:'rgba(255,255,255,0.08)', borderRadius:20, padding:'6px 16px', color:'#32CD89', textDecoration:'none', display:'flex', alignItems:'center', gap:6}}>
                    {link.icon ? (
                      <img src={link.icon.startsWith('http') ? link.icon : resolveMediaUrl(link.icon)} alt={link.name} style={{width:24,height:24,marginRight:6}} />
                    ) : (
                      (() => {
                        const p = matchPlatform(link.url, platforms);
                        if (p && p.icon) {
                          const iconSrc = p.icon.startsWith('http') ? p.icon : resolveMediaUrl(p.icon);
                          return <img src={iconSrc} alt={p.name} style={{width:24,height:24,marginRight:6}} />;
                        }
                        return null;
                      })()
                    )}
                    <span style={{fontWeight:500}}>{link.name}</span>
                  </a>
                ))
              ) : (
                <span style={{color:'#888'}}>Keine Social-Links hinterlegt.</span>
              )}
            </div>
            {user.canSeeMembership !== false && (
              <div style={{opacity:0.7, marginTop:8}}>
                Mitglied seit {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
              </div>
            )}
            <div style={{marginTop:12, marginBottom:12, fontWeight:500, fontSize:16}}>
              <span style={{marginRight:18}}>
                Lifetime-Punkte: <span style={{color:'#32CD89'}}>{points.lifetime}</span>
                {lifetimeRank === 1 && <span title="1. Platz" style={{marginLeft:8,fontSize:22}}>🥇</span>}
                {lifetimeRank === 2 && <span title="2. Platz" style={{marginLeft:8,fontSize:22}}>🥈</span>}
                {lifetimeRank === 3 && <span title="3. Platz" style={{marginLeft:8,fontSize:22}}>🥉</span>}
              </span>
              <span>
                Wöchentliche Punkte: <span style={{color:'#32CD89'}}>{points.weekly}</span>
                {weeklyRank === 1 && <span title="1. Platz" style={{marginLeft:8,fontSize:22}}>🥇</span>}
                {weeklyRank === 2 && <span title="2. Platz" style={{marginLeft:8,fontSize:22}}>🥈</span>}
                {weeklyRank === 3 && <span title="3. Platz" style={{marginLeft:8,fontSize:22}}>🥉</span>}
              </span>
            </div>

            {user.easterEggs && typeof user.easterEggs === 'object' && (
              <div style={{marginTop:-6, marginBottom:12, opacity:0.9}}>
                Oster‑Eier gefunden: <span style={{color:'#32CD89', fontWeight:700}}>{user.easterEggs.found}</span> / {user.easterEggs.total}
              </div>
            )}
            {user.canSeeMembership !== false && (
              <div style={{opacity:0.7, marginTop:4}}>
                {user.roles && user.roles.length > 0 && (
                  <span>Rollen: {user.roles.map(r => <span key={r} style={{background:'rgba(50,205,137,0.12)', color:'#32CD89', borderRadius:12, padding:'2px 10px', marginRight:6, fontWeight:500}}>{r}</span>)}</span>
                )}
              </div>
            )}

            {/* Abzeichen-Anzeige */}
            <div style={{marginTop:18}}>
              <h3 style={{fontSize:18, fontWeight:600, marginBottom:8, textAlign:'center'}}>Abzeichen</h3>
              {badges.length === 0 ? (
                <span style={{color:'#888'}}>Noch keine Abzeichen erhalten.</span>
              ) : (
                <div style={{display:'flex', gap:16, flexWrap:'wrap', justifyContent:'center'}}>
                  {[...badges].sort((a, b) => {
                    const idxA = a.Badge?.sortIndex ?? 100;
                    const idxB = b.Badge?.sortIndex ?? 100;
                    return idxA - idxB;
                  }).map(ub => {
                    if (!ub.Badge) return null;
                    const levels = Array.isArray(ub.Badge.BadgeLevels) ? ub.Badge.BadgeLevels : [];
                    // Level aus UserBadge.note extrahieren (z.B. "Level 1")
                    let userLevel = null;
                    if (ub.note && ub.note.startsWith('Level ')) {
                      const lvlNum = parseInt(ub.note.replace('Level ', ''), 10);
                      userLevel = levels.find(l => l.level === lvlNum);
                    }
                    // Fallback: höchstes Level falls note fehlt
                    const displayLevel = userLevel || levels.sort((a,b)=>b.level-a.level)[0];
                    // Tooltip-Text: Beschreibung des Levels oder Badge
                    const tooltip = displayLevel?.description || ub.Badge.description || '';
                    return (
                      <div key={ub.id} style={{display:'flex', flexDirection:'column', alignItems:'center', background:'#222', borderRadius:12, padding:10, minWidth:90}}>
                        <img src={displayLevel?.icon || ub.Badge.icon} alt={ub.Badge.name} style={{width:48, height:48, marginBottom:6}} title={tooltip} />
                        <span style={{fontWeight:600, fontSize:15}} title={tooltip}>{ub.Badge.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Live Embed (Twitch / YouTube / TikTok) */}
            {livePlayer && livePlayer.src && (
              <div style={{marginTop:24}}>
                <h3 style={{fontSize:18, fontWeight:600, marginBottom:8, textAlign:'center'}}>{livePlayer.title}</h3>
                <div style={{width:'50%', margin:'0 auto'}}>
                  <div style={{position:'relative', paddingTop:'56.25%', borderRadius:12, overflow:'hidden', boxShadow:'0 4px 16px rgba(0,0,0,0.25)'}}>
                  <iframe
                    title={`Live Player`}
                    src={livePlayer.src}
                    frameBorder="0"
                    allowFullScreen
                    scrolling="no"
                    style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}}
                  />
                  </div>
                </div>
                {livePlayer.link && (
                  <div style={{marginTop:8, textAlign:'center'}}>
                    <a href={livePlayer.link} target="_blank" rel="noopener" style={{color:'#32CD89', textDecoration:'underline'}}>Stream in neuem Tab öffnen</a>
                  </div>
                )}
              </div>
            )}
            {/* Shops des Nutzers */}
            {Array.isArray(shops) && shops.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h3 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10, textAlign:'center' }}>Shops</h3>
                <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:16 }}>
                  {shops.map(s => (
                    <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
                      style={{ flex:'0 1 300px', maxWidth:360, display:'block', textDecoration:'none', color:'#fff', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, overflow:'hidden' }}>
                      {s.logoUrl && (
                        <div style={{ width:'100%', background:'#111' }}>
                          <img src={s.logoUrl} alt="" style={{ width:'100%', height:'auto', display:'block' }} />
                        </div>
                      )}
                      <div style={{ padding:12 }}>
                        {!s.logoUrl && (
                          <div style={{ width:'100%', height:80, borderRadius:8, background:'#222', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:8 }}>
                            <span style={{ fontSize:24, fontWeight:700 }}>{s.name?.[0]?.toUpperCase()||'S'}</span>
                          </div>
                        )}
                        <div style={{ fontWeight:600 }}>{s.name}</div>
                        {s.description && <div style={{ fontSize:12, color:'#bbb', marginTop:4 }}>{s.description}</div>}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
            {/* Recent social content below live player (or here if no live) */}
            {socialContent && (
              <div style={{ marginTop: 18 }}>
                <h3 style={{ fontSize: 24, fontWeight: 900, marginBottom: 8, textAlign:'center' }}>Neuste Inhalte</h3>
                <div style={{ display:'grid', gap:16 }}>
                  {/* Resolve view mode: combined vs per-channel from viewed user's settings */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode === 'per-channel') return null;
                    return (
                      <>
                        {/* Twitch VODs */}
                        {Array.isArray(socialContent.twitch?.vods) && socialContent.twitch.vods.length > 0 && (
                          <div>
                            <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>Twitch VoD's</div>
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                              {socialContent.twitch.vods.slice(0,3).map(v => (
                                <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                                  <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', transform:'translateZ(0)', transition:'transform 120ms ease, box-shadow 120ms ease' }} onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.02)';e.currentTarget.style.boxShadow='0 6px 16px rgba(0,0,0,0.35)';}} onMouseLeave={e=>{e.currentTarget.style.transform='scale(1.0)';e.currentTarget.style.boxShadow='none';}}>
                                    <img loading="lazy" src={resolveImageUrl(v.thumbnailUrl)} alt={v.title} referrerPolicy="no-referrer" style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />
                                    <span style={{ position:'absolute', top:8, left:8, background:'rgba(0,0,0,0.45)', padding:'2px 6px', borderRadius:6, fontSize:12, border:'1px solid rgba(255,255,255,0.25)' }}>VOD</span>
                                    {v.duration && <span style={{ position:'absolute', bottom:6, right:6, background:'rgba(0,0,0,0.55)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>{v.duration}</span>}
                                  </div>
                                  <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title}</div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Twitch Clips */}
                        {Array.isArray(socialContent.twitch?.clips) && socialContent.twitch.clips.length > 0 && (
                          <div>
                            <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>Twitch Clips</div>
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                              {socialContent.twitch.clips.slice(0,3).map(c => (
                                <a key={c.id} href={c.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                                  <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', transform:'translateZ(0)', transition:'transform 120ms ease, box-shadow 120ms ease' }} onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.02)';e.currentTarget.style.boxShadow='0 6px 16px rgba(0,0,0,0.35)';}} onMouseLeave={e=>{e.currentTarget.style.transform='scale(1.0)';e.currentTarget.style.boxShadow='none';}}>
                                    <img loading="lazy" src={resolveImageUrl(c.thumbnailUrl)} alt={c.title} referrerPolicy="no-referrer" style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />
                                    <span style={{ position:'absolute', top:8, left:8, background:'rgba(0,0,0,0.45)', padding:'2px 6px', borderRadius:6, fontSize:12, border:'1px solid rgba(255,255,255,0.25)' }}>Clip</span>
                                    {c.duration && <span style={{ position:'absolute', bottom:6, right:6, background:'rgba(0,0,0,0.55)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>{c.duration}s</span>}
                                  </div>
                                  <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.title}</div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {/* Per-channel mode for Twitch */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode !== 'per-channel') return null;
                    const groups = Array.isArray(socialContent.twitchByChannel) ? socialContent.twitchByChannel : [];
                    if (groups.length === 0) return null;
                    return groups.map((g, idx) => (
                      <div key={`tw-${g.login||idx}`}>
                        <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>Twitch – {g.login}</div>
                        {Array.isArray(g.vods) && g.vods.length > 0 && (
                          <div style={{ marginBottom:10 }}>
                            <div style={{ fontSize: 16, fontWeight:600, marginBottom:6 }}>VoDs</div>
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                              {g.vods.slice(0,3).map(v => (
                                <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                                  <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56' }}>
                                    <img loading="lazy" src={resolveImageUrl(v.thumbnailUrl)} alt={v.title} referrerPolicy="no-referrer" style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />
                                  </div>
                                  <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title}</div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        {Array.isArray(g.clips) && g.clips.length > 0 && (
                          <div>
                            <div style={{ fontSize: 16, fontWeight:600, marginBottom:6 }}>Clips</div>
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                              {g.clips.slice(0,3).map(c => (
                                <a key={c.id} href={c.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                                  <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56' }}>
                                    <img loading="lazy" src={resolveImageUrl(c.thumbnailUrl)} alt={c.title} referrerPolicy="no-referrer" style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />
                                  </div>
                                  <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.title}</div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                  {/* TikTok – combined */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode === 'per-channel') return null;
                    return (Array.isArray(socialContent.tiktok?.videos) && socialContent.tiktok.videos.length > 0) ? (
                    <div>
                      <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>TikTok</div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                        {socialContent.tiktok.videos.slice(0,3).map(v => (
                          <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                            <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', transform:'translateZ(0)', transition:'transform 120ms ease, box-shadow 120ms ease', paddingTop:'177.78%' }} onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.02)';e.currentTarget.style.boxShadow='0 6px 16px rgba(0,0,0,0.35)';}} onMouseLeave={e=>{e.currentTarget.style.transform='scale(1.0)';e.currentTarget.style.boxShadow='none';}}>
                              <img loading="lazy" src={resolveImageUrl(v.thumbnailUrl)} alt={v.title} referrerPolicy="no-referrer" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                              <span style={{ position:'absolute', top:8, left:8, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.25)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>TikTok</span>
                            </div>
                            <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title || 'TikTok-Video'}</div>
                          </a>
                        ))}
                      </div>
                    </div>
                    ) : null;
                  })()}
                  {/* TikTok – per profile */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode !== 'per-channel') return null;
                    const groups = Array.isArray(socialContent.tiktokByProfile) ? socialContent.tiktokByProfile : [];
                    if (groups.length === 0) return null;
                    return groups.map((g, idx) => (
                      <div key={`tt-${idx}`}>
                        <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>TikTok – {g.url}</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                          {(g.videos||[]).slice(0,3).map(v => (
                            <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                              <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', transform:'translateZ(0)', transition:'transform 120ms ease, box-shadow 120ms ease', paddingTop:'177.78%' }}>
                                <img loading="lazy" src={resolveImageUrl(v.thumbnailUrl)} alt={v.title} referrerPolicy="no-referrer" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                                <span style={{ position:'absolute', top:8, left:8, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.25)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>TikTok</span>
                              </div>
                              <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title || 'TikTok-Video'}</div>
                            </a>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                  {/* YouTube Videos – combined */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode === 'per-channel') return null;
                    return (Array.isArray(socialContent.youtube?.videos) && socialContent.youtube.videos.length > 0) ? (
                    <div>
                      <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>YouTube Videos</div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                        {socialContent.youtube.videos.slice(0,3).map(v => (
                          <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                            <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', transform:'translateZ(0)', transition:'transform 120ms ease, box-shadow 120ms ease' }} onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.02)';e.currentTarget.style.boxShadow='0 6px 16px rgba(0,0,0,0.35)';}} onMouseLeave={e=>{e.currentTarget.style.transform='scale(1.0)';e.currentTarget.style.boxShadow='none';}}>
                              <img loading="lazy" src={resolveImageUrl(v.thumbnailUrl)} alt={v.title} referrerPolicy="no-referrer" style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />
                              <span style={{ position:'absolute', top:8, left:8, background:'rgba(0,0,0,0.45)', padding:'2px 6px', borderRadius:6, fontSize:12, border:'1px solid rgba(255,255,255,0.25)' }}>Video</span>
                            </div>
                            <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title}</div>
                          </a>
                        ))}
                      </div>
                    </div>
                    ) : null;
                  })()}
                  {/* YouTube Shorts – combined */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode === 'per-channel') return null;
                    return (Array.isArray(socialContent.youtube?.shorts) && socialContent.youtube.shorts.length > 0) ? (
                    <div>
                      <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>YouTube Shorts</div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                        {socialContent.youtube.shorts.slice(0,3).map(v => (
                          <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                            <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', transform:'translateZ(0)', transition:'transform 120ms ease, box-shadow 120ms ease', paddingTop:'177.78%' }} onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.02)';e.currentTarget.style.boxShadow='0 6px 16px rgba(0,0,0,0.35)';}} onMouseLeave={e=>{e.currentTarget.style.transform='scale(1.0)';e.currentTarget.style.boxShadow='none';}}>
                              <img loading="lazy" src={resolveImageUrl(v.thumbnailUrl)} alt={v.title} referrerPolicy="no-referrer" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                              <span style={{ position:'absolute', top:8, left:8, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.25)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>Short</span>
                            </div>
                            <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title}</div>
                          </a>
                        ))}
                      </div>
                    </div>
                    ) : null;
                  })()}
                  {/* YouTube – per channel */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode !== 'per-channel') return null;
                    const groups = Array.isArray(socialContent.youtubeByChannel) ? socialContent.youtubeByChannel : [];
                    if (groups.length === 0) return null;
                    return groups.map((g, idx) => (
                      <div key={`yt-${idx}`}>
                        <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>YouTube – {g.url}</div>
                        {Array.isArray(g.videos) && g.videos.length > 0 && (
                          <div style={{ marginBottom:10 }}>
                            <div style={{ fontSize: 16, fontWeight:600, marginBottom:6 }}>Videos</div>
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                              {g.videos.slice(0,3).map(v => (
                                <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                                  <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56' }}>
                                    <img loading="lazy" src={resolveImageUrl(v.thumbnailUrl)} alt={v.title} referrerPolicy="no-referrer" style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />
                                  </div>
                                  <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title}</div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        {Array.isArray(g.shorts) && g.shorts.length > 0 && (
                          <div>
                            <div style={{ fontSize: 16, fontWeight:600, marginBottom:6 }}>Shorts</div>
                            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                              {g.shorts.slice(0,3).map(v => (
                                <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                                  <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', paddingTop:'177.78%' }}>
                                    <img loading="lazy" src={resolveImageUrl(v.thumbnailUrl)} alt={v.title} referrerPolicy="no-referrer" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                                  </div>
                                  <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title}</div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                  {/* Instagram Reels (9:16) */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode === 'per-channel') return null;
                    const ig = Array.isArray(socialContent.instagram?.posts) ? socialContent.instagram.posts : [];
                    const reels = ig.filter(p => p && p.isReel);
                    if (reels.length === 0) return null;
                    return (
                      <div>
                        <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>Instagram Reels</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                          {reels.slice(0,3).map(p => (
                            <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                              <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', transform:'translateZ(0)', transition:'transform 120ms ease, box-shadow 120ms ease', paddingTop:'177.78%' }}>
                                <img loading="lazy" src={resolveImageUrl(p.thumbnailUrl)} alt={p.title || 'Instagram Reel'} referrerPolicy="no-referrer" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                                <span style={{ position:'absolute', top:8, left:8, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.25)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>Reel</span>
                              </div>
                              <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.title || 'Instagram Reel'}</div>
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Instagram Beiträge (einheitlich, quadratisch 1:1) */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode === 'per-channel') return null;
                    const ig = Array.isArray(socialContent.instagram?.posts) ? socialContent.instagram.posts : [];
                    const posts = ig.filter(p => p && !p.isReel);
                    if (posts.length === 0) return null;
                    return (
                      <div>
                        <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>Instagram Beiträge</div>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                          {posts.slice(0,3).map(p => (
                            <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                              <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', transform:'translateZ(0)', transition:'transform 120ms ease, box-shadow 120ms ease', paddingTop:'100%' }}>
                                <img loading="lazy" src={resolveImageUrl(p.thumbnailUrl)} alt={p.title || 'Instagram'} referrerPolicy="no-referrer" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                                <span style={{ position:'absolute', top:8, left:8, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.25)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>Instagram</span>
                              </div>
                              <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.title || 'Instagram-Post'}</div>
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  {/* Instagram – per profile */}
                  {(() => {
                    const mode = user?.settings?.socialViewMode || 'combined';
                    if (mode !== 'per-channel') return null;
                    const groups = Array.isArray(socialContent.instagramByProfile) ? socialContent.instagramByProfile : [];
                    if (groups.length === 0) return null;
                    return groups.map((g, idx) => {
                      const reels = (g.posts||[]).filter(p => p && p.isReel);
                      const posts = (g.posts||[]).filter(p => p && !p.isReel);
                      return (
                        <div key={`ig-${idx}`}>
                          <div style={{ fontSize: 22, fontWeight:700, marginBottom:8 }}>Instagram – {g.url}</div>
                          {reels.length > 0 && (
                            <div style={{ marginBottom:10 }}>
                              <div style={{ fontSize: 16, fontWeight:600, marginBottom:6 }}>Reels</div>
                              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                                {reels.slice(0,3).map(p => (
                                  <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                                    <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', paddingTop:'177.78%' }}>
                                      <img loading="lazy" src={resolveImageUrl(p.thumbnailUrl)} alt={p.title || 'Instagram Reel'} referrerPolicy="no-referrer" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                                    </div>
                                    <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.title || 'Instagram Reel'}</div>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                          {posts.length > 0 && (
                            <div>
                              <div style={{ fontSize: 16, fontWeight:600, marginBottom:6 }}>Beiträge</div>
                              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12 }}>
                                {posts.slice(0,3).map(p => (
                                  <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                                    <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', paddingTop:'100%' }}>
                                      <img loading="lazy" src={resolveImageUrl(p.thumbnailUrl)} alt={p.title || 'Instagram'} referrerPolicy="no-referrer" style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                                    </div>
                                    <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.title || 'Instagram-Post'}</div>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Icon-Logik jetzt über matchPlatform und Plattform-Objekt
